import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Alert, Button, Field } from '../components/ui';
import { useServices } from '../lib/context';

const loginRoute = getRouteApi('/login');

// "El agente como nodo" (Doc. 02/03 §05): the agent at the centre, its capabilities around it.
const SATELLITES: [label: string, x: number, y: number, color: string][] = [
  ['Conocimiento', 180, 36, 'cyan'],
  ['Herramientas', 318, 108, 'violet'],
  ['Acciones', 318, 252, 'emerald'],
  ['Canales', 180, 324, 'cyan'],
  ['Memoria', 42, 252, 'amber'],
  ['Gobierno', 42, 108, 'indigo'],
];

function NodeGraph() {
  return (
    <svg
      className="node-graph"
      viewBox="0 0 360 360"
      role="img"
      aria-label="El agente en el centro, conectado con conocimiento, herramientas, acciones, canales, memoria y gobierno."
    >
      {SATELLITES.map(([label, x, y, c]) => (
        <line
          key={`l-${label}`}
          x1="180"
          y1="180"
          x2={x}
          y2={y}
          stroke={`var(--c-${c})`}
          strokeOpacity=".35"
          strokeDasharray="4 4"
        />
      ))}
      {SATELLITES.map(([label, x, y, c]) => (
        <g key={label}>
          <circle cx={x} cy={y} r="7" fill="var(--c-black)" stroke={`var(--c-${c})`} strokeWidth="1.5" />
          <text x={x} y={y + (y > 180 ? 24 : -16)} textAnchor="middle">
            {label}
          </text>
        </g>
      ))}
      <circle cx="180" cy="180" r="34" fill="var(--c-deep)" stroke="var(--c-cyan)" strokeWidth="1.5" />
      <text x="180" y="184" textAnchor="middle" className="center-label">
        Agente
      </text>
    </svg>
  );
}

export function LoginPage() {
  const { auth, config } = useServices();
  const { returnTo, error } = loginRoute.useSearch();
  const [pending, setPending] = useState<string | null>(null);
  const [email, setEmail] = useState('');

  useEffect(() => {
    document.title = 'Iniciar sesión · Agents by Stratika';
  }, []);

  const start = async (provider?: string) => {
    setPending(provider ?? 'default');
    try {
      await auth.signIn({ provider, returnTo });
    } catch {
      setPending(null);
    }
  };

  const devSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (email) void auth.signIn({ email, returnTo });
  };

  return (
    <main className="auth">
      <section className="brand-panel" aria-label="Agents by Stratika">
        <div className="wordmark">
          <strong>
            Agents<span>.</span>
          </strong>
          <small>by Stratika</small>
        </div>
        <div>
          <div className="eyebrow">Plataforma de agentes</div>
          <h2>
            Crea, despliega y escala agentes que <em>realmente pueden actuar.</em>
          </h2>
        </div>
        <NodeGraph />
        <div className="foot">Build. Connect. Automate. Scale.</div>
      </section>

      <section className="auth-form">
        <div className="auth-card">
          <div>
            <h1>Inicia sesión</h1>
            <p>Usa la cuenta corporativa con la que te invitaron a tu espacio de trabajo.</p>
          </div>

          {error && (
            <Alert tone="danger" role="alert" title="No pudimos completar el inicio de sesión">
              <p>Intenta de nuevo. Si el problema continúa, contacta al administrador de tu espacio.</p>
            </Alert>
          )}

          {auth.mode === 'dev' ? (
            <form className="stack" style={{ gap: 12 }} onSubmit={devSubmit}>
              <Field
                label="Correo (modo desarrollo)"
                help="Solo disponible en local. No hay verificación de identidad."
              >
                {(p) => (
                  <input
                    {...p}
                    className="input"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="nombre@empresa.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                )}
              </Field>
              <Button variant="primary" type="submit" style={{ minHeight: 46 }}>
                Continuar
              </Button>
            </form>
          ) : (
            <div className="auth-actions">
              {auth.providers.map((p, i) => (
                <Button
                  key={p.id}
                  variant={i === 0 ? 'primary' : 'secondary'}
                  loading={pending === p.id}
                  disabled={pending !== null}
                  onClick={() => void start(p.id)}
                >
                  Continuar con {p.label}
                </Button>
              ))}
              {auth.providers.length === 0 && (
                <Button variant="primary" loading={pending !== null} onClick={() => void start()}>
                  Continuar
                </Button>
              )}
            </div>
          )}

          <p className="auth-foot">
            {config.environment !== 'production' && (
              <>
                Ambiente <span className="mono">{config.environment}</span>.{' '}
              </>
            )}
            Al continuar aceptas las políticas de uso de Stratika.
          </p>
        </div>
      </section>
    </main>
  );
}

export function CallbackPage() {
  const { auth } = useServices();
  const navigate = useNavigate();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    auth
      .completeSignIn()
      .then((to) => navigate({ to, replace: true }))
      .catch(() => navigate({ to: '/login', search: { error: 'callback' }, replace: true }));
  }, [auth, navigate]);

  return (
    <main className="auth" data-single aria-busy="true">
      <p className="muted" style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
        <span className="spinner" aria-hidden="true" />
        Completando inicio de sesión…
      </p>
    </main>
  );
}
